(() => {
  if (window.__avGiftProgressInitialized) return;
  window.__avGiftProgressInitialized = true;

  const CONFIG = {
    threshold: 6000,
    giftProductId: 16663595286873,
    giftDoradoVariantId: 59703093756249,
    giftPlateadoVariantId: 59703093789017,
  };

  const VALID_GIFTS = new Set([CONFIG.giftDoradoVariantId, CONFIG.giftPlateadoVariantId]);
  let syncing = false;
  let rerunRequested = false;
  let scheduledSync = null;
  let preferredGiftVariantId = null;

  try {
    const stored = Number(sessionStorage.getItem('av_gift_variant'));
    if (VALID_GIFTS.has(stored)) preferredGiftVariantId = stored;
  } catch (_) {}

  const route = (key, fallback) => (window.routes && window.routes[key]) || fallback;

  async function fetchCart() {
    const response = await fetch(`${route('cart_url', '/cart')}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
    return response.json();
  }

  function isGiftLine(item) {
    return Number(item.product_id) === CONFIG.giftProductId || item.properties?._av_gift === '1';
  }

  function giftLines(cart) {
    return (cart.items || []).filter(isGiftLine);
  }

  function paidSubtotal(cart) {
    return (cart.items || []).reduce((sum, item) => {
      if (isGiftLine(item)) return sum;
      return sum + Number(item.final_line_price || 0);
    }, 0);
  }

  function paidItemCount(cart) {
    return (cart.items || []).filter((item) => !isGiftLine(item) && Number(item.quantity) > 0).length;
  }

  function existingGiftVariant(cart) {
    const line = giftLines(cart).find((item) => VALID_GIFTS.has(Number(item.variant_id)));
    return line ? Number(line.variant_id) : null;
  }

  function selectedGiftVariant(cart) {
    if (VALID_GIFTS.has(preferredGiftVariantId)) return preferredGiftVariantId;
    const existing = existingGiftVariant(cart);
    if (VALID_GIFTS.has(existing)) return existing;
    return CONFIG.giftDoradoVariantId;
  }

  function desiredGiftVariant(cart, subtotal) {
    if (subtotal < CONFIG.threshold) return null;
    return selectedGiftVariant(cart);
  }

  function money(cents, currency = 'EUR') {
    const locale = document.documentElement.lang || 'es-ES';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  }

  function setChoiceState(root, variantId) {
    root.querySelectorAll('[data-av-gift-choice]').forEach((button) => {
      const active = Number(button.dataset.variantId) === Number(variantId);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  function updateUI(cart) {
    const root = document.querySelector('[data-av-gift-progress]');
    if (!root) return;

    const subtotal = paidSubtotal(cart);
    const desired = desiredGiftVariant(cart, subtotal);
    const copy = root.querySelector('[data-av-gift-copy]');
    const fill = root.querySelector('[data-av-gift-fill]');
    const track = root.querySelector('[role="progressbar"]');
    const unlocked = root.querySelector('[data-av-gift-unlocked]');
    const originalPrice = root.querySelector('[data-av-gift-price-original]');

    root.hidden = paidItemCount(cart) === 0;
    const percent = Math.max(0, Math.min(100, (subtotal / CONFIG.threshold) * 100));
    if (fill) fill.style.width = `${percent}%`;
    if (track) track.setAttribute('aria-valuenow', String(Math.min(60, subtotal / 100)));

    const configuredPrice = Number(root.dataset.giftPrice || 1995);
    if (originalPrice) originalPrice.textContent = money(configuredPrice, cart.currency || 'EUR');

    if (subtotal < CONFIG.threshold) {
      if (copy) copy.textContent = `Te faltan ${money(CONFIG.threshold - subtotal, cart.currency || 'EUR')} para elegir tus Pendientes Alba de regalo`;
      if (unlocked) unlocked.hidden = true;
      return;
    }

    if (copy) copy.textContent = 'Has desbloqueado tus Pendientes Alba de regalo';
    if (unlocked) unlocked.hidden = false;
    setChoiceState(root, desired);
  }

  async function updateGiftLines(cart, desiredVariantId) {
    const lines = giftLines(cart);
    let keptDesired = false;
    const updates = {};

    for (const line of lines) {
      const isDesired = desiredVariantId && Number(line.variant_id) === Number(desiredVariantId);
      if (isDesired && !keptDesired) {
        keptDesired = true;
        if (Number(line.quantity) !== 1) updates[line.key] = 1;
      } else {
        updates[line.key] = 0;
      }
    }

    if (Object.keys(updates).length) {
      const response = await fetch(route('cart_update_url', '/cart/update.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (!response.ok) throw new Error(`Gift cart update failed: ${response.status}`);
      cart = await response.json();
    }

    if (desiredVariantId && !keptDesired) {
      const finish = Number(desiredVariantId) === CONFIG.giftPlateadoVariantId ? 'Plateado' : 'Dorado';
      const response = await fetch(route('cart_add_url', '/cart/add.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{
            id: desiredVariantId,
            quantity: 1,
            properties: {
              _av_gift: '1',
              _av_gift_finish: finish,
            },
          }],
        }),
      });
      if (!response.ok) throw new Error(`Gift add failed: ${response.status}`);
      await response.json();
      cart = await fetchCart();
    }

    return cart;
  }

  function publishRefresh(cart) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'av-gift',
        cartData: cart,
      });
    }
  }

  async function syncGift() {
    if (syncing) {
      rerunRequested = true;
      return;
    }

    syncing = true;
    try {
      let cart = await fetchCart();
      if (!VALID_GIFTS.has(preferredGiftVariantId)) {
        const existing = existingGiftVariant(cart);
        if (VALID_GIFTS.has(existing)) preferredGiftVariantId = existing;
      }

      updateUI(cart);
      const subtotal = paidSubtotal(cart);
      const desired = desiredGiftVariant(cart, subtotal);
      const before = giftLines(cart).map((item) => `${item.key}:${item.variant_id}:${item.quantity}`).sort().join('|');

      cart = await updateGiftLines(cart, desired);

      const after = giftLines(cart).map((item) => `${item.key}:${item.variant_id}:${item.quantity}`).sort().join('|');
      updateUI(cart);
      if (before !== after) publishRefresh(cart);
    } catch (error) {
      console.error('[Alba Varen gift progress]', error);
    } finally {
      syncing = false;
      if (rerunRequested) {
        rerunRequested = false;
        queueMicrotask(syncGift);
      }
    }
  }

  function scheduleSync() {
    if (scheduledSync) clearTimeout(scheduledSync);
    scheduledSync = setTimeout(() => {
      scheduledSync = null;
      syncGift();
    }, 0);
  }

  function subscribeToCart() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.source === 'av-gift') return;
        scheduleSync();
      });
    }

    document.addEventListener('cart:refresh', scheduleSync);
    window.addEventListener('pageshow', scheduleSync);

    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-av-gift-choice]');
      if (!button) return;
      event.preventDefault();
      const variantId = Number(button.dataset.variantId);
      if (!VALID_GIFTS.has(variantId)) return;

      preferredGiftVariantId = variantId;
      try { sessionStorage.setItem('av_gift_variant', String(variantId)); } catch (_) {}

      const root = button.closest('[data-av-gift-progress]');
      if (root) setChoiceState(root, variantId);
      scheduleSync();
    });
  }

  const start = () => {
    subscribeToCart();
    syncGift();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();